import { EQAuthParamType } from "src/Types";
import { Response, Select2WebElementType } from "src/Types/ReDoc";
import { EQueueConfigType } from "src/Types/ReDoc/EQueue";
import { ReturnQueueCountType, ReturnQueueItemType, ReturnStateType } from "src/Types/ReDoc/Return";

export default class ReturnAPI {
	static _urls = {
		loadConfig: "/Nvx.ReDoc.ReturnEQ/LoadConfig",
		saveConfig: "/Nvx.ReDoc.ReturnEQ/SaveConfig",
		start: "/Nvx.ReDoc.ReturnEQ/Start",
		pultStatus: "/Nvx.ReDoc.ReturnEQ/PultStatus",
		getQueueState: "/Nvx.ReDoc.ReturnEQ/QueueCount",
		queue: "/Nvx.ReDoc.ReturnEQ/Queue",
		previewNext: "/Nvx.ReDoc.ReturnEQ/PreviewNext",
		confirmCall: "/Nvx.ReDoc.ReturnEQ/ConfirmCall",
		call: "/Nvx.ReDoc.ReturnEQ/InviteNext",
		callById: "/Nvx.ReDoc.ReturnEQ/InviteById",
		replaceSelected: "/Nvx.ReDoc.ReturnEQ/ReplaceSelected",
		beginServing: "/Nvx.ReDoc.ReturnEQ/BeginServing",
		complete: "/Nvx.ReDoc.ReturnEQ/Complete",
		finish: "/Nvx.ReDoc.ReturnEQ/Finish",
		closeWorkday: "/Nvx.ReDoc.ReturnEQ/CloseWorkday",
		transfer: "/Nvx.ReDoc.ReturnEQ/Transfer",
		windows: "/Nvx.ReDoc.ReturnEQ/Windows",
		beginBreak: "/Nvx.ReDoc.ReturnEQ/BeginBreak",
		beginBreakFromCalling: "/Nvx.ReDoc.ReturnEQ/BeginBreakFromCalling",
		endBreak: "/Nvx.ReDoc.ReturnEQ/EndBreak",
		cancelCall: "/Nvx.ReDoc.ReturnEQ/CancelCall",
		reject: "/Nvx.ReDoc.ReturnEQ/Reject",
		recall: "/Nvx.ReDoc.ReturnEQ/Recall"
	};

	/** Получение конфигурации ЭО */
	static getConfig = async (): Promise<Response<EQueueConfigType>> => {
		try {
			const url = window.host + this._urls.loadConfig;
			const request = await fetch(url, { credentials: "include" });
			if (request.status === 200) {
				const response = await request.json() as Response<Record<string, unknown>>;
				const source = (response.result ?? {}) as Record<string, unknown>;
				const readFlag = (camel: string, pascal: string): boolean => {
					const value = source[camel] ?? source[pascal];
					return value === undefined ? true : Boolean(value);
				};
				return {
					...response,
					result: {
						contactId: "",
						login: "",
						password: "",
						officeId: (source.officeId ?? source.OfficeId ?? -1) as number,
						pultId: String(source.windowIp ?? source.WindowIp ?? ""),
						pultName: null,
						queueServerAddress: null,
						autoCallEnabled: readFlag("autoCallEnabled", "AutoCallEnabled"),
						callByTicketNumberEnabled: readFlag("callByTicketNumberEnabled", "CallByTicketNumberEnabled"),
						callAnotherEnabled: readFlag("callAnotherEnabled", "CallAnotherEnabled"),
						scheduleEnabled: readFlag("scheduleEnabled", "ScheduleEnabled"),
						transferEnabled: readFlag("transferEnabled", "TransferEnabled")
					}
				};
			}
			throw new Error("Ошибка при получении настроек ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка при получении настроек ЭО.");
		}
	};

	/** Сохранение конфигурации ЭО */
	static setConfig = async (params: EQAuthParamType): Promise<Response<null>> => {
		const frmData = new FormData();
		frmData.append("rt", "dfd");
		frmData.append("windowIp", params.pultId);
		try {
			const url = window.host + this._urls.saveConfig;
			const request = await fetch(url, { method: "POST", credentials: "include", body: frmData });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка при сохранении настроек ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка при сохранении настроек ЭО.");
		}
	};

	/** Старт сессии ЭО */
	static start = async (id: string): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.start;
			const frmData = new FormData();
			frmData.append("id", id);
			const request = await fetch(url, { method: "POST", credentials: "include", body: frmData });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка входа в электронную очередь.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка входа в электронную очередь.");
		}
	};

	/** Запрос статуса ЭО */
	static getStatus = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.pultStatus;
			const request = await fetch(url, { method: "GET", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка получения статуса ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка получения статуса ЭО.");
		}
	};

	/** Запрос состояния очереди */
	static getQueueState = async (): Promise<Response<ReturnQueueCountType>> => {
		try {
			const url = window.host + this._urls.getQueueState;
			const request = await fetch(url, { method: "GET", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка получения состояния очереди ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка получения состояния очереди ЭО.");
		}
	};

	/** Получение общей очереди */
	static getQueue = async (term: string = ""): Promise<Response<ReturnQueueItemType[]>> => {
		try {
			const params = new URLSearchParams();
			if (term.trim()) {
				params.append("term", term);
			}
			const query = params.toString();
			const url = `${window.host}${this._urls.queue}${query ? `?${query}` : ""}`;
			const request = await fetch(url, { method: "GET", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка получения очереди.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка получения очереди.");
		}
	};

	/** Вызов посетителя по идентификатору талона */
	static callById = async (ticketId: string): Promise<Response<ReturnStateType>> => {
		try {
			const url = `${window.host}${this._urls.callById}?ticketId=${encodeURIComponent(ticketId)}`;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка вызова посетителя по талону.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка вызова посетителя по талону.");
		}
	};

	/** Вызов следующего посетителя по очереди */
	static call = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.call;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка вызова следующего посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка вызова следующего посетителя.");
		}
	};

	/** Предварительный выбор следующего посетителя без фактического вызова */
	static previewNext = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.previewNext;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка выбора следующего посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка выбора следующего посетителя.");
		}
	};

	/** Подтверждение вызова предварительно выбранного посетителя */
	static confirmCall = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.confirmCall;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка подтверждения вызова посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка подтверждения вызова посетителя.");
		}
	};

	/** Начало обслуживания текущего посетителя */
	static beginServing = async (ticketId?: string): Promise<Response<ReturnStateType>> => {
		try {
			const query = ticketId ? `?ticketId=${encodeURIComponent(ticketId)}` : "";
			const url = window.host + this._urls.beginServing + query;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка начала обслуживания посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка начала обслуживания посетителя.");
		}
	};

	/** Завершение обслуживания текущего посетителя */
	static complete = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.complete;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка завершения обслуживания посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка завершения обслуживания посетителя.");
		}
	};

	/** Завершение сессии ЭО (legacy) */
	static finish = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.finish;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка завершения сеанса ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка завершения сеанса ЭО.");
		}
	};

	/** Безопасное завершение рабочего дня оператора */
	static closeWorkday = async (source: "manual" | "inactivity" | "logout" = "manual"): Promise<Response<ReturnStateType>> => {
		try {
			const url = `${window.host}${this._urls.closeWorkday}?source=${encodeURIComponent(source)}`;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка завершения рабочего дня.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка завершения рабочего дня.");
		}
	};

	/** Перенаправить посетителя в другое окно */
	static transfer = async (toWindow: string, comment: string): Promise<Response<ReturnStateType>> => {
		try {
			const query = `toWindow=${encodeURIComponent(toWindow)}&comment=${encodeURIComponent(comment)}`;
			const url = `${window.host}${this._urls.transfer}?${query}`;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error("Ошибка перенаправления посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка перенаправления посетителя.");
		}
	};

	/** Начало перерыва из состояния Waiting */
	static beginBreak = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.beginBreak;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка запроса на перерыв ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка запроса на перерыв ЭО.");
		}
	};

	/** Атомарный переход Calling → OnPause: отменяет вызов, затем ставит перерыв */
	static beginBreakFromCalling = async (reason: string): Promise<Response<ReturnStateType>> => {
		try {
			const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
			const url = `${window.host}${this._urls.beginBreakFromCalling}${query}`;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка перехода на перерыв из состояния вызова.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка перехода на перерыв из состояния вызова.");
		}
	};

	/** Замена выбранного талона: снимает резерв A (если есть), вызывает талон B */
	static replaceSelected = async (ticketId: string): Promise<Response<ReturnStateType>> => {
		try {
			const url = `${window.host}${this._urls.replaceSelected}?ticketId=${encodeURIComponent(ticketId)}`;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка замены выбранного посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка замены выбранного посетителя.");
		}
	};

	/** Завершение перерыва */
	static endBreak = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.endBreak;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка запроса на окончание перерыва ЭО.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка запроса на окончание перерыва ЭО.");
		}
	};

	static cancelCall = async (reason: string): Promise<Response<ReturnStateType>> => {
		try {
			const query = `reason=${encodeURIComponent(reason)}`;
			const url = `${window.host}${this._urls.cancelCall}?${query}`;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка отмены вызова посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка отмены вызова посетителя.");
		}
	};

	/** Неявка посетителя */
	static reject = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.reject;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка запроса неявки посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка запроса неявки посетителя.");
		}
	};

	/** Повторный вызов посетителя (оповещение на табло) */
	static recall = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.recall;
			const request = await fetch(url, { method: "POST", credentials: "include" });
			if (request.status === 200) return await request.json();
			throw new Error("Ошибка повторного вызова посетителя.");
		}
		catch (e) {
			console.warn(e);
			throw new Error("Ошибка повторного вызова посетителя.");
		}
	};

	/** Получение доступных окон для перенаправления */
	static getWindows = async (transfer: boolean = true, officeId?: number): Promise<Select2WebElementType[]> => {
		try {
			const params = new URLSearchParams({ transfer: String(transfer) });
			if (typeof officeId === "number" && officeId > 0) {
				params.append("officeId", String(officeId));
			}
			const url = `${window.host}${this._urls.windows}?${params.toString()}`;
			const request = await fetch(url, { method: "GET", credentials: "include" });
			if (request.status === 200) {
				const data = await request.json();
				if (Array.isArray(data)) {
					return data;
				}
				if (data?.hasError) {
					throw new Error(data.errorMessage || "Ошибка получения списка окон.");
				}
				return [];
			}
			throw new Error("Ошибка получения списка окон.");
		}
		catch (e) {
			console.warn(e);
			throw new Error(e instanceof Error ? e.message : "Ошибка получения списка окон.");
		}
	};
}
